import styled from '@emotion/styled';
import { Region } from '@kaizenplatform/hugot-entity';
import { Meta } from '@kaizenplatform/hugot-entity/dist/entity/DynamicSchemaDefinition/IDynamicSchemaSection';
import { Button, Dialog, DialogActions, DialogContent as _DialogContent } from '@material-ui/core';
import { saveAs } from 'file-saver';
import { createElement, FC, MouseEvent, MouseEventHandler, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useToggle } from 'react-use';

import HttpClient from '../../../adapter/HttpClient';
import { SearchAssetResult } from '../../../entity/SearchAsset';
import { PreparedMaterialSelector } from './SearchAsset/PreparedMaterialSelector';

type Props = {
  region: Region;
  isOpen: boolean;
  onClose: MouseEventHandler<HTMLElement>;
};

const getMeta = (region: Region): Meta => {
  if (region === 'jp') {
    return {
      allowSearchTypes: ['audio'],
    };
  } else {
    return {
      allowSearchTypes: ['image', 'video', 'audio'],
    };
  }
};

const generateTempId = (): string =>
  `${new Date().getTime().toString(16)}-${Math.floor(10000 * Math.random()).toString(16)}`;

const downloadFile = async (
  _: MouseEvent<HTMLElement>,
  selectedSearchAssetResult: SearchAssetResult[number],
): Promise<void> => {
  const extractFileNameResult = /^https:\/\/(.+\/)*(.+__kz__)?([^?]+)(.*fm=([a-zA-Z]+))?/.exec(
    selectedSearchAssetResult.originalUrl,
  );

  const fileName =
    extractFileNameResult != null
      ? `${decodeURI(extractFileNameResult[3])}${
          extractFileNameResult[5] != null ? `.${extractFileNameResult[5]}` : ''
        }`
      : `search_file_${generateTempId()}`;

  const client = new HttpClient({ baseUrl: '' });
  const response = await client.get(selectedSearchAssetResult.originalUrl, {
    withCredentials: false,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: response.data.type });
  saveAs(blob, fileName);
};

const SearchFileUploaderDialog: FC<Props> = ({ isOpen, onClose, region }) => {
  const [selectedSearchAsset, setSelectedSearchAsset] = useState<SearchAssetResult[number] | null>(null);
  const [isDownloading, toggleIsDownloading] = useToggle(false);

  const meta = getMeta(region);

  return (
    <Dialog onClose={onClose} open={isOpen} maxWidth="lg" fullWidth={true}>
      <DialogContent>
        <PreparedMaterialSelector
          meta={meta}
          region={region}
          selectedSearchAsset={selectedSearchAsset}
          setSelectedSearchAsset={setSelectedSearchAsset}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          size="small"
          color="default"
          onClick={(e) => {
            setSelectedSearchAsset(null);
            onClose(e);
          }}
        >
          <FormattedMessage id="search_file_uploader.selector.cancel" />
        </Button>
        <Button
          disabled={selectedSearchAsset == null || isDownloading === true}
          variant="contained"
          size="small"
          color="primary"
          onClick={async (e) => {
            if (selectedSearchAsset == null) {
              return;
            }
            toggleIsDownloading(true);
            await downloadFile(e, selectedSearchAsset);
            toggleIsDownloading(false);
          }}
        >
          {isDownloading ? (
            <FormattedMessage id="search_file_uploader.selector.submit.wait" />
          ) : (
            <FormattedMessage id="search_file_uploader.selector.submit" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogContent = styled(_DialogContent)({
  minWidth: 960,
});

export default SearchFileUploaderDialog;
